import { useCallback, useEffect, useMemo } from 'react';
import { FieldErrors, UseFormClearErrors, UseFormSetError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SidebarTabNamesType } from '@pages/Admin/Campaign/ui/types';
import { useStartStopContext } from '@features/Admin/Campaign/contexts/useStartStop.context';
import {
  getCampaignStartBlockers,
  getIssuedTabsFromBlockers,
  getPrimaryDisabledReason,
  getTabIssueMessages,
} from '@features/Admin/Campaign/utils/campaign-validation.utils';
import { CampaignTypeEnum } from '@shared/constants/campaign.constants';
import { VITE_SKIP_INBOUND_SIP_CONFIG } from '@shared/constants/environments';
import { CampaignDto, SipTrunkResponseDto } from '@shared/services/apiService/apiService';
import { CampaignValidationFields } from '@shared/types/admin/campaign';

import { CreateCampaignFormValue } from '../model/type';

interface UseCampaignStartValidationOptions {
  campaignData?: CampaignDto;
  hasRealCustomers: boolean;
  campaignSipTrunk?: SipTrunkResponseDto;
  formStateErrors: FieldErrors<CreateCampaignFormValue>;
  hasAgents: boolean;
  isLoading: boolean;
  isLoadingCampaignAgents: boolean;
  isRunningLoading: boolean;
  isStoppingLoading: boolean;
  currentUserRole?: string;
  setError: UseFormSetError<CreateCampaignFormValue>;
  clearErrors: UseFormClearErrors<CreateCampaignFormValue>;
}

export const useCampaignStartValidation = ({
  campaignData,
  hasRealCustomers: realCustomerExist,
  campaignSipTrunk,
  formStateErrors,
  hasAgents,
  isLoading,
  isLoadingCampaignAgents,
  isRunningLoading,
  isStoppingLoading,
  currentUserRole,
  setError,
  clearErrors,
}: UseCampaignStartValidationOptions) => {
  const { t } = useTranslation();
  const { setIsDisabledCampaignRun, setDisabledReason } = useStartStopContext();

  const hasConfiguredPhoneNumbers = useMemo(() => {
    const hasSipNumbers = Boolean(campaignSipTrunk?.numbers?.length);
    const isInbound = campaignData?.type === CampaignTypeEnum.INBOUND;

    return (
      realCustomerExist ||
      (isInbound && hasSipNumbers) ||
      (isInbound && VITE_SKIP_INBOUND_SIP_CONFIG)
    );
  }, [campaignData, campaignSipTrunk, realCustomerExist]);

  const campaignStartBlockers = useMemo(
    () =>
      getCampaignStartBlockers({
        isRunningLoading,
        isLoading,
        isLoadingCampaignAgents,
        isStoppingLoading,
        campaignStatus: campaignData?.status as string,
        hasAgents,
        hasConfiguredPhoneNumbers,
        currentUserRole,
        formErrors: formStateErrors,
      }),
    [
      isRunningLoading,
      isLoading,
      isLoadingCampaignAgents,
      isStoppingLoading,
      campaignData?.status,
      hasAgents,
      hasConfiguredPhoneNumbers,
      currentUserRole,
      formStateErrors,
    ],
  );

  const issuedTabs = useMemo(
    () => getIssuedTabsFromBlockers(campaignStartBlockers),
    [campaignStartBlockers],
  );

  const tabIssueMessages = useMemo(
    () => getTabIssueMessages(campaignStartBlockers, t),
    [campaignStartBlockers, t],
  );

  const forceValidate = useCallback(() => {
    if (!hasAgents) {
      setError('agents' as CampaignValidationFields, { type: 'required' });
      return;
    }
    clearErrors('agents' as CampaignValidationFields);
  }, [clearErrors, hasAgents, setError]);

  useEffect(() => {
    if (!campaignData) return;

    if (!hasAgents) {
      setError('agents' as CampaignValidationFields, { type: 'required' });
    } else {
      clearErrors('agents' as CampaignValidationFields);
    }
  }, [campaignData, clearErrors, hasAgents, setError]);

  useEffect(() => {
    setIsDisabledCampaignRun(campaignStartBlockers.length > 0);
    setDisabledReason(getPrimaryDisabledReason(campaignStartBlockers, t));
  }, [campaignStartBlockers, setDisabledReason, setIsDisabledCampaignRun, t]);

  return {
    campaignStartBlockers,
    issuedTabs: issuedTabs as SidebarTabNamesType[],
    tabIssueMessages,
    forceValidate,
  };
};
